<template>
    <div>
        <!-- dialogAddMaterial -->
        <v-dialog v-model="dialogAddMaterial" persistent max-width="400" @keydown.esc="dialogAddMaterial = false">
            <v-card>
                <div class="d-flex py-1 primary">
                    <v-card-title
                        class="text-uppercase flex-grow-1 text-subtitle-1 justify-center pa-0 white--text"
                    >
                        {{ $t('specializations.linking-materials-to-specializations') }}
                    </v-card-title>

                    <v-btn icon @click="dialogAddMaterial = false">
                        <v-icon color="white"> mdi-close-circle-outline </v-icon>
                    </v-btn>
                </div>

                <v-card-text class="pt-2 px-3 pb-0">
                    <v-row dense class="ma-0">
                        <v-col>
                            <v-autocomplete 
                                v-model="materialIds"
                                :items="materials"
                                item-text="name"
                                item-value="id"
                                hide-details
                                dense
                                outlined
                                multiple
                                :label="$t('inputs.materials')"
                                clearable
                            >
                                <template v-slot:selection="{ item, index }">
                                    <span v-if="index < 1" class="me-1">{{ item.name + ', ' }}</span>
                                    <span
                                        v-if="index === 1"
                                        class="grey--text text-caption"
                                    >
                                        (+{{ materialIds.length - 1 }})
                                    </span>
                                </template>
                            </v-autocomplete>
                        </v-col>
                    </v-row>
                </v-card-text>

                <v-card-actions class="justify-end">
                    <v-btn
                        @click="updateMaterialForSpe()"
                        text
                        color="success"
                        :loading="submitLoading"
                    >
                        {{$t('save')}}
                    </v-btn>
                    <v-btn
                        text
                        :disabled="submitLoading"
                        @click="dialogAddMaterial = false"
                    >
                        {{$t('cancel')}}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <div class="d-flex justify-space-between my-1">
            <div>
                <!-- add specialty -->
                <v-btn
                    v-if="isSuperAdmin || permissionsUser.Entities === roleName.action"
                	small
                	class="mx-2"
                	color="light-blue white--text"
                	@click="specialtyName = null; specialtyId = null; dialog = true;"
                    :disabled="loading"
                >
                    <v-icon class="me-2">
                        mdi-plus-circle-outline
                    </v-icon>
                    {{ $t('specializations.add-new-specialty') }}
                </v-btn>

                <!-- BACK -->
                <v-btn
                    color="grey darken-2 white--text"
                    small
                    :to="{ name: 'customers' }"
                    :disabled="loading"
                >
                    <v-icon small class="me-1"> mdi-arrow-left-circle </v-icon>
                    {{ $t('back') }}
                </v-btn>
            </div>
        </div>

        <!-- add/edit dialog -->
        <v-dialog
            transition="dialog-transition"
            max-width="300"
            v-model="dialog"
            persistent
            @keydown.esc="dialog = false"
        >
            <v-form @submit.prevent="submit" ref="form">
                <v-card>
                    <div class="d-flex py-1 primary">
                        <v-card-title
                            class="text-uppercase flex-grow-1 text-subtitle-1 justify-center pa-0 white--text"
                        >
                            {{ specialtyId ? $t('specializations.edit-specialty') : $t('specializations.add-new-specialty') }}
                        </v-card-title>

                        <v-btn icon @click="dialog = false">
                            <v-icon color="white"> mdi-close-circle-outline </v-icon>
                        </v-btn>
                    </div>
                    <v-card-text class="pt-2 px-3 pb-0">
                        <v-row dense class="ma-0">
                            <!-- Name -->
                            <v-col cols="12" class="py-1">
                                <v-text-field
                                    :label="$t('inputs.specialty')"
                                    clearable
                                    v-model="specialtyName"
                                    :rules="rules.required"
                                    :disabled="loading"
                                    required
                                    outlined
                                    dense
                                    hide-details="auto"
                                ></v-text-field>
                            </v-col>
                        </v-row>
                    </v-card-text>

                    <v-card-actions class="justify-end">
                        <v-btn
                            type="submit"
                            text
                            color="success"
                            :loading="submitLoading"
                        >
                            {{$t('save')}}
                        </v-btn>
                        <v-btn
                            text
                            :disabled="submitLoading"
                            @click="dialog = false"
                        >
                            {{$t('cancel')}}
                        </v-btn>
                    </v-card-actions>
                </v-card>
            </v-form>
        </v-dialog>

        <delete-dialog
            ref="deleteDialog"
            success-message="Specialty is deleted"
            @on-delete="fetchSpecialties"
        >
            {{ $t('are-you-sure-you-want-to') }} <span class="red--text">{{ $t('delete') }}</span> 
            {{ $t('specializations.this-specialty') + $t('?')}}
        </delete-dialog>

        <!-- table -->
        <bee-handy-table
            :headers="headers"
            :items="!loading ? specialties : []"
            :loading="loading"
            fixed-header
            :height="$vuetify.breakpoint.height - 100"
            pagination-on-scroll
            dense
            zebra
        >
            <template v-slot:item.action="{ item }">
                <v-btn 
                    v-if="linkingMaterialsToSpecializations"
                    @click="specialtyId = item.id; dialogAddMaterial = true"
                    icon
                	small
                	color="success"
                >   
                    <v-icon> mdi-plus-circle </v-icon>
                </v-btn>
                <v-btn
                	icon
                	small
                	color="edit-color"
                	@click="specialtyName = item.name; specialtyId = item.id; dialog = true;"
                >
                    <v-icon>mdi-pencil</v-icon>
                </v-btn>
                <v-btn
                	icon
                	small
                	color="alert-color"
                	@click="$refs.deleteDialog.activeDeleteDialog(item.id, 'specialties')"
                >
                    <v-icon>mdi-delete</v-icon>
                </v-btn>
            </template>
        </bee-handy-table>
    </div>
</template>

<script>
import rules from '@/validation rules'
import DeleteDialog from '@/components/DeleteDialog'
import { mapState } from 'vuex'
import { roleName } from '@/helpers/enums'
import { linkingMaterialsToSpecializations } from '@/configs/global.config'

export default {
    name: 'SpecializationsManagement',

    components: {
        DeleteDialog
    },
    watch: {
        dialogAddMaterial (val) {
            if (!val) {
                this.materialIds = []
            }
        }
    },
    data(){
        return {
            loading: false,
            submitLoading: false,
            
            specialtyId: null,
            dialog: false,
            rules,

            // data
            specialtyName: null,
            roleName,
            linkingMaterialsToSpecializations,
            dialogAddMaterial: false,
            materialIds: []
        }
    },

    computed:{
        ...mapState({
            specialties: state => state.specialties.specialties,
			isSuperAdmin: state => state.auth.isSuperAdmin,
            materials: state => state.materials.materials,
            permissionsUser: state => state.auth.youPermissions
        }),

        headers() {
            const headers = [
                { 
                    text: this.$t('headers.specialty'),
                    align: 'start',
                    value: 'name',
                    class:'text-body-2 font-weight-bold',
                },
            ];
            if (this.isSuperAdmin) {
                headers.push({

                    text: '', 
                    name: 'action',
                    align: 'center',
                    value: 'action',
                    class:'text-body-2 font-weight-bold',
                    sortable: false,
             
                })
            }
            return headers
        }
    },

    methods: {
        fetchSpecialties() {
            this.loading = true;

            return this.$store.dispatch('specialties/fetchAll').finally(() => {
                this.loading = false;
            });
        },

        submit() {
            this.submitLoading = true;
            const actionName = !this.specialtyId ? 'create' : 'update';

            this.$store.dispatch(`specialties/${actionName}`, {
                id: this.specialtyId,
                specialty: { name: this.specialtyName }
            }).then(() => {
                this.dialog = false;
                this.fetchSpecialties();
            }).finally(() => {
                this.submitLoading = false;
            });
        },
        
        updateMaterialForSpe() {
            this.submitLoading = true
            const id = this.specialtyId
            this.$store.dispatch('materials/updateMaterialsForSpecialty', { id, materials: this.materialIds }).finally(() => {
                this.submitLoading = false
                this.dialogAddMaterial = false
            })
        }
    },

    created(){
        if (!this.specialties.length) {
            this.fetchSpecialties()
        }
        if (!this.materials.length && linkingMaterialsToSpecializations) {
            this.$store.dispatch('materials/fetchAll')
        }
    },

    metaInfo: {
        title: 'Specializations management',
        titleTemplate: '%s | Octopharma'
    }
}
</script>